import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import "./DailyPurchases.css";

const ComparatifTroisFournisseurs = () => {
  const [benchList, setBenchList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliersList, setSuppliersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCat, setSelectedCat] = useState("all");

  // Modal d'édition/ajout
  const [showModal, setShowModal] = useState(false);
  const [editingBench, setEditingBench] = useState(null);
  const [saving, setSaving] = useState(false);

  // Formulaire 3 Fournisseurs
  const [formData, setFormData] = useState({
    articleName: "",
    categoryName: "Fournitures de bureau",
    subCategoryName: "",
    unit: "pièce",
    supplier1: { supplierName: "Fournisseur A", unitPrice: 0, minQuantity: 1, deliveryDelay: "24h", notes: "" },
    supplier2: { supplierName: "Fournisseur B", unitPrice: 0, minQuantity: 1, deliveryDelay: "48h", notes: "" },
    supplier3: { supplierName: "Fournisseur C", unitPrice: 0, minQuantity: 1, deliveryDelay: "Immédiat", notes: "" },
    notes: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [benchRes, catRes, supRes1, supRes2] = await Promise.allSettled([
        axios.get("/api/daily-purchases/supplier-bench"),
        axios.get("/api/daily-purchases/categories"),
        axios.get("/api/suppliers/getSuppliers"),
        axios.get("/api/SuppliersV/getSuppliers")
      ]);

      if (benchRes.status === "fulfilled") setBenchList(benchRes.value.data || []);
      if (catRes.status === "fulfilled") setCategories(catRes.value.data || []);

      const supCombined = [];
      if (supRes1.status === "fulfilled" && Array.isArray(supRes1.value.data)) {
        supRes1.value.data.forEach((s) => {
          const name = s.companyName || s.name;
          if (name && !supCombined.includes(name)) supCombined.push(name);
        });
      }
      if (supRes2.status === "fulfilled" && Array.isArray(supRes2.value.data)) {
        supRes2.value.data.forEach((s) => {
          const name = s.companyName || s.name;
          if (name && !supCombined.includes(name)) supCombined.push(name);
        });
      }
      setSuppliersList(supCombined.sort());
    } catch (err) {
      console.error("Erreur chargement comparatif 3 fournisseurs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenAdd = () => {
    setEditingBench(null);
    setFormData({
      articleName: "",
      categoryName: categories[0]?.name || "Fournitures de bureau",
      subCategoryName: "",
      unit: "pièce",
      supplier1: { supplierName: "Fournisseur A", unitPrice: 0, minQuantity: 1, deliveryDelay: "24h", notes: "" },
      supplier2: { supplierName: "Fournisseur B", unitPrice: 0, minQuantity: 1, deliveryDelay: "48h", notes: "" },
      supplier3: { supplierName: "Fournisseur C", unitPrice: 0, minQuantity: 1, deliveryDelay: "Immédiat", notes: "" },
      notes: ""
    });
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingBench(item);
    setFormData({
      _id: item._id,
      articleName: item.articleName,
      categoryName: item.categoryName || "Fournitures de bureau",
      subCategoryName: item.subCategoryName || "",
      unit: item.unit || "pièce",
      supplier1: item.supplier1 || { supplierName: "Fournisseur A", unitPrice: 0 },
      supplier2: item.supplier2 || { supplierName: "Fournisseur B", unitPrice: 0 },
      supplier3: item.supplier3 || { supplierName: "Fournisseur C", unitPrice: 0 },
      notes: item.notes || ""
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.articleName.trim()) {
      alert("Le nom de l'article est requis");
      return;
    }

    setSaving(true);
    try {
      await axios.post("/api/daily-purchases/supplier-bench", formData);
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error("Erreur sauvegarde comparatif:", err);
      alert("Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous supprimer cet article du comparatif ?")) return;
    try {
      await axios.delete(`/api/daily-purchases/supplier-bench/${id}`);
      fetchData();
    } catch (err) {
      console.error("Erreur suppression benchmark:", err);
    }
  };

  // Filtrage
  const filteredList = benchList.filter((item) => {
    const matchCat = selectedCat === "all" || item.categoryName === selectedCat;
    const matchSearch =
      !search.trim() ||
      item.articleName.toLowerCase().includes(search.toLowerCase()) ||
      item.supplier1?.supplierName?.toLowerCase().includes(search.toLowerCase()) ||
      item.supplier2?.supplierName?.toLowerCase().includes(search.toLowerCase()) ||
      item.supplier3?.supplierName?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="dp-card">
      <div className="dp-card-header">
        <div>
          <h3 className="dp-card-title">
            ⚖️ Comparatif des Prix d'Achat (3 Fournisseurs)
          </h3>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
            Comparez les tarifs unitaires de 3 fournisseurs différents pour identifier instantanément la meilleure offre sur chaque article.
          </p>
        </div>

        <div className="dp-filters-bar">
          <select
            className="dp-filter-select"
            value={selectedCat}
            onChange={(e) => setSelectedCat(e.target.value)}
          >
            <option value="all">Toutes les familles</option>
            {categories.map((c) => (
              <option key={c._id} value={c.name}>
                {c.icon} {c.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            className="dp-filter-input"
            placeholder="🔍 Filtrer article ou fournisseur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button className="dp-btn dp-btn-primary" onClick={handleOpenAdd}>
            ➕ Ajouter un Article au Comparatif
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 font-weight-bold">Chargement des comparatifs...</p>
        </div>
      ) : filteredList.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
          <span style={{ fontSize: "3rem" }}>⚖️</span>
          <h5>Aucun comparatif disponible</h5>
          <p>Ajoutez un premier article pour comparer les offres de vos fournisseurs.</p>
          <button className="dp-btn dp-btn-primary mt-2" onClick={handleOpenAdd}>
            ➕ Ajouter un comparatif
          </button>
        </div>
      ) : (
        <div className="dp-bench-matrix">
          {filteredList.map((item) => {
            const p1 = Number(item.supplier1?.unitPrice) || 0;
            const p2 = Number(item.supplier2?.unitPrice) || 0;
            const p3 = Number(item.supplier3?.unitPrice) || 0;

            const validPrices = [
              { idx: 1, p: p1, name: item.supplier1?.supplierName },
              { idx: 2, p: p2, name: item.supplier2?.supplierName },
              { idx: 3, p: p3, name: item.supplier3?.supplierName }
            ].filter((x) => x.p > 0);

            const minPrice = validPrices.length > 0 ? Math.min(...validPrices.map((x) => x.p)) : 0;
            const maxPrice = validPrices.length > 0 ? Math.max(...validPrices.map((x) => x.p)) : 0;
            const savings = maxPrice > minPrice ? maxPrice - minPrice : 0;

            return (
              <div key={item._id} className="dp-bench-card">
                <div className="dp-bench-header">
                  <div>
                    <div className="dp-bench-article-title">{item.articleName}</div>
                    <div className="dp-bench-category">
                      {item.categoryName} • Unité : <strong>{item.unit}</strong>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "6px" }}>
                    <button
                      className="dp-btn dp-btn-secondary"
                      style={{ padding: "4px 8px", fontSize: "0.78rem" }}
                      onClick={() => handleOpenEdit(item)}
                      title="Modifier les prix"
                    >
                      ✏️
                    </button>
                    <button
                      className="dp-btn dp-btn-secondary"
                      style={{ padding: "4px 8px", fontSize: "0.78rem" }}
                      onClick={() => handleDelete(item._id)}
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* 3 Colonnes Fournisseurs */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {/* Fournisseur 1 */}
                  <div className={`dp-supplier-col ${p1 > 0 && p1 === minPrice ? "is-best" : ""}`}>
                    <div className="dp-sup-name-row">
                      <span className="dp-sup-name">
                        1️⃣ {item.supplier1?.supplierName || "Fournisseur A"}
                      </span>
                      <span className="dp-sup-price">
                        {p1 > 0 ? `${p1.toFixed(3)} DT` : "—"}
                      </span>
                    </div>
                    <div className="dp-sup-meta">
                      {item.supplier1?.deliveryDelay && (
                        <span>⏱️ Délai: {item.supplier1.deliveryDelay}</span>
                      )}
                      {item.supplier1?.notes && <span>💬 {item.supplier1.notes}</span>}
                    </div>
                    {p1 > 0 && p1 === minPrice && (
                      <div className="mt-1">
                        <span className="dp-badge dp-badge-best">🏆 Meilleur Prix</span>
                      </div>
                    )}
                  </div>

                  {/* Fournisseur 2 */}
                  <div className={`dp-supplier-col ${p2 > 0 && p2 === minPrice ? "is-best" : ""}`}>
                    <div className="dp-sup-name-row">
                      <span className="dp-sup-name">
                        2️⃣ {item.supplier2?.supplierName || "Fournisseur B"}
                      </span>
                      <span className="dp-sup-price">
                        {p2 > 0 ? `${p2.toFixed(3)} DT` : "—"}
                      </span>
                    </div>
                    <div className="dp-sup-meta">
                      {item.supplier2?.deliveryDelay && (
                        <span>⏱️ Délai: {item.supplier2.deliveryDelay}</span>
                      )}
                      {item.supplier2?.notes && <span>💬 {item.supplier2.notes}</span>}
                    </div>
                    {p2 > 0 && p2 === minPrice && (
                      <div className="mt-1">
                        <span className="dp-badge dp-badge-best">🏆 Meilleur Prix</span>
                      </div>
                    )}
                  </div>

                  {/* Fournisseur 3 */}
                  <div className={`dp-supplier-col ${p3 > 0 && p3 === minPrice ? "is-best" : ""}`}>
                    <div className="dp-sup-name-row">
                      <span className="dp-sup-name">
                        3️⃣ {item.supplier3?.supplierName || "Fournisseur C"}
                      </span>
                      <span className="dp-sup-price">
                        {p3 > 0 ? `${p3.toFixed(3)} DT` : "—"}
                      </span>
                    </div>
                    <div className="dp-sup-meta">
                      {item.supplier3?.deliveryDelay && (
                        <span>⏱️ Délai: {item.supplier3.deliveryDelay}</span>
                      )}
                      {item.supplier3?.notes && <span>💬 {item.supplier3.notes}</span>}
                    </div>
                    {p3 > 0 && p3 === minPrice && (
                      <div className="mt-1">
                        <span className="dp-badge dp-badge-best">🏆 Meilleur Prix</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Synthèse économie */}
                {savings > 0 && (
                  <div
                    style={{
                      background: "#f0fdf4",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      fontSize: "0.82rem",
                      color: "#166534",
                      fontWeight: "700",
                      display: "flex",
                      justifyContent: "space-between"
                    }}
                  >
                    <span>💡 Économie potentielle :</span>
                    <span>+{savings.toFixed(3)} DT / {item.unit}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── MODAL AJOUT / ÉDITION COMPARATIF ── */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        dialogClassName="dp-modal-screen-width"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: "800" }}>
            {editingBench ? "✏️ Modifier le Comparatif Prix" : "➕ Nouvel Article au Comparatif"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label font-weight-bold">Nom de l'article *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: Rouleau caisse 80mm, Stylo bille..."
                value={formData.articleName}
                onChange={(e) => setFormData({ ...formData, articleName: e.target.value })}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label font-weight-bold">Famille</label>
              <select
                className="form-control"
                value={formData.categoryName}
                onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
              >
                {categories.map((c) => (
                  <option key={c._id} value={c.name}>
                    {c.icon} {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label font-weight-bold">Unité</label>
              <input
                type="text"
                className="form-control"
                placeholder="pièce, rouleau..."
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              />
            </div>
          </div>

          <datalist id="dp-registered-suppliers-bench">
            {suppliersList.map((sup, sIdx) => (
              <option key={sIdx} value={sup} />
            ))}
          </datalist>

          <h6 style={{ fontWeight: "800", color: "#0f172a", marginTop: "16px", marginBottom: "12px" }}>
            🏪 Renseigner les Tarifs des 3 Fournisseurs (ou choisir dans la liste) :
          </h6>

          {/* Fournisseur 1 */}
          <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "10px", marginBottom: "12px" }}>
            <div className="row g-2">
              <div className="col-md-5">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Fournisseur 1 (Nom)
                </label>
                <input
                  type="text"
                  list="dp-registered-suppliers-bench"
                  placeholder="Sélectionner ou saisir..."
                  className="form-control form-control-sm"
                  value={formData.supplier1?.supplierName || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier1: { ...formData.supplier1, supplierName: e.target.value }
                    })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Prix Unitaire (DT)
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="form-control form-control-sm"
                  value={formData.supplier1?.unitPrice || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier1: { ...formData.supplier1, unitPrice: parseFloat(e.target.value) || 0 }
                    })
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Délai / Notes
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="24h, livraison offerte..."
                  value={formData.supplier1?.notes || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier1: { ...formData.supplier1, notes: e.target.value }
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Fournisseur 2 */}
          <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "10px", marginBottom: "12px" }}>
            <div className="row g-2">
              <div className="col-md-5">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Fournisseur 2 (Nom)
                </label>
                <input
                  type="text"
                  list="dp-registered-suppliers-bench"
                  placeholder="Sélectionner ou saisir..."
                  className="form-control form-control-sm"
                  value={formData.supplier2?.supplierName || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier2: { ...formData.supplier2, supplierName: e.target.value }
                    })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Prix Unitaire (DT)
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="form-control form-control-sm"
                  value={formData.supplier2?.unitPrice || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier2: { ...formData.supplier2, unitPrice: parseFloat(e.target.value) || 0 }
                    })
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Délai / Notes
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="48h, min 50 unités..."
                  value={formData.supplier2?.notes || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier2: { ...formData.supplier2, notes: e.target.value }
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Fournisseur 3 */}
          <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "10px", marginBottom: "12px" }}>
            <div className="row g-2">
              <div className="col-md-5">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Fournisseur 3 (Nom)
                </label>
                <input
                  type="text"
                  list="dp-registered-suppliers-bench"
                  placeholder="Sélectionner ou saisir..."
                  className="form-control form-control-sm"
                  value={formData.supplier3?.supplierName || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier3: { ...formData.supplier3, supplierName: e.target.value }
                    })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Prix Unitaire (DT)
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="form-control form-control-sm"
                  value={formData.supplier3?.unitPrice || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier3: { ...formData.supplier3, unitPrice: parseFloat(e.target.value) || 0 }
                    })
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Délai / Notes
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Direct en magasin..."
                  value={formData.supplier3?.notes || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      supplier3: { ...formData.supplier3, notes: e.target.value }
                    })
                  }
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Sauvegarde..." : "💾 Enregistrer le comparatif"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ComparatifTroisFournisseurs;
